// Scheduler heartbeat. Main-thread timers are throttled in background tabs
// (to 1 s or worse), which would starve a 120 ms look-ahead; timers inside a
// dedicated worker are not, so the tick comes from a tiny inline worker when
// possible, with setInterval as the fallback (e.g. under a strict CSP).

export interface Ticker {
  start(): void;
  stop(): void;
  dispose(): void;
}

const WORKER_SRC = 'let id=0;onmessage=function(e){clearInterval(id);if(e.data>0)id=setInterval(function(){postMessage(0)},e.data)};';

export function createTicker(intervalMs: number, cb: () => void): Ticker {
  let worker: Worker | null = null;
  let url: string | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let running = false;

  const useInterval = () => {
    if (timer === null && running) timer = setInterval(cb, intervalMs);
  };

  try {
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL.createObjectURL === 'function') {
      url = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
      worker = new Worker(url);
      worker.onmessage = () => {
        if (running) cb();
      };
      worker.onerror = () => {
        worker?.terminate();
        worker = null;
        useInterval();
      };
    }
  } catch {
    worker = null;
  }

  return {
    start() {
      running = true;
      if (worker) worker.postMessage(intervalMs);
      else useInterval();
    },
    stop() {
      running = false;
      worker?.postMessage(0);
      if (timer !== null) clearInterval(timer);
      timer = null;
    },
    dispose() {
      this.stop();
      worker?.terminate();
      worker = null;
      if (url) URL.revokeObjectURL(url);
      url = null;
    },
  };
}
